import {useReducer, useRef} from 'react';

import {isNil} from 'ramda';

import {isDefined} from 'shared/web';

import {PARALLEL_UPLOADS} from '../constants/parallelUploads';
import {GetChunkFn} from '../types/GetChunkFn';
import {HandleMultiMultiPartUploadType} from '../types/HandleMultiMultiPartUploadType';
import {QueueVideoTask} from '../types/QueueVideoTask';
import {QueueVideoTaskJob} from '../types/QueueVideoTaskJob';
import {UploadApi} from '../types/UploadApi';
import {VideoUploadError} from '../types/VideoUploadError';
import {convertFileToQueueTask} from '../utils/convertFileToQueueTask';
import {enhanceWithCountOfParts} from '../utils/enhanceWithCountOfParts';
import {getUploadProgress} from '../utils/getUploadProgress';
import {handleUploadChunk} from '../utils/handleUploadChunk';
import {TaskActionKind as ActionKind, taskReducer} from '../utils/taskReducer';
import {usePreSignLinksManager} from './usePreSignLinksManager';
import {useVideoCompleteManager} from './useVideoCompleteManager';

export function useVideoUploadManager(
  api: UploadApi,
  errorCallback: (videoUploadError: VideoUploadError, error?: Error) => void
) {
  const [{tasks}, dispatch] = useReducer(taskReducer, {tasks: []});
  // One controller per task, keyed by task id, so a removed or cleared task can cancel the chunk
  // uploads it still has in flight. Kept outside the reducer state: controllers are not data, and
  // the async chains in `processTask` need the live instance whichever render they started in.
  const abortControllersRef = useRef(new Map<string, AbortController>());
  // Bumped by every `clearTasks`. A presign call that is out when the queue is cleared has no task
  // to abort yet; comparing generations lets its late response be dropped instead of re-adding a
  // task into the cleared queue.
  const clearGenerationRef = useRef(0);
  const {getPreSignLinks} = usePreSignLinksManager(api.sendMultiPartUploadRequest);
  const {handleTaskCompleted} = useVideoCompleteManager(api.sendMultipartUploadCompleted);

  const addTask = (task: QueueVideoTask) => {
    abortControllersRef.current.set(task.id, new AbortController());
    dispatch({type: ActionKind.ADD_TASK, payload: {task}});
  };

  const removeTask = (id: string) => {
    abortControllersRef.current.get(id)?.abort();
    abortControllersRef.current.delete(id);
    dispatch({type: ActionKind.REMOVE_TASK, payload: {id}});
  };

  /**
   * Drops every task and cancels the chunk uploads they still have in flight. For the moment the
   * owner of the queue goes away with work still running — in CarAudit a logout — where finishing
   * the upload would only produce a file nobody is going to attach.
   */
  const clearTasks = () => {
    clearGenerationRef.current += 1;
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
    dispatch({type: ActionKind.CLEAR_TASKS});
  };

  const handleError = (
    videoUploadError: VideoUploadError,
    taskConnectionFileId?: string,
    error?: Error
  ) => {
    errorCallback(videoUploadError, error);
    if (isDefined(taskConnectionFileId)) {
      updateTaskStatus(taskConnectionFileId, 'ERROR');
    }
  };

  /**
   * Retrieves the progress of each task.
   */
  const uploadProgress = tasks
    .map((task) => {
      const doneJobsLength = task.jobs.filter((j) => j.status === 'DONE').length;
      const allJobsLength = task.jobs.length;

      return allJobsLength > 0
        ? {
            id: task.id,
            progress: getUploadProgress(doneJobsLength / allJobsLength),
            connectionFileId: task.connectionFileId,
          }
        : null;
    })
    .filter(isDefined);

  /**
   * Initiates a multipart upload process for a file.
   */
  const handleMultiMultiPartUpload: HandleMultiMultiPartUploadType = async (
    rawFile,
    callback,
    onError,
    onProgress
  ) => {
    const file = enhanceWithCountOfParts(rawFile);
    const chunkSize = file.chunkSize;

    if (isNil(chunkSize)) {
      return Promise.resolve(errorCallback(VideoUploadError.MAX_VIDEO_SIZE));
    }

    const clearGeneration = clearGenerationRef.current;

    try {
      const uploadInfo = await getPreSignLinks(file);

      if (clearGeneration !== clearGenerationRef.current) {
        // The queue was cleared while the presign call was out: nobody is waiting for this task.
        return;
      }

      const task = convertFileToQueueTask(
        {...file, chunkSize},
        uploadInfo,
        callback,
        onError,
        onProgress
      );
      addTask(task);

      return task;
    } catch {
      // Same as a late success: a failure for an upload the queue no longer holds is not reported.
      if (clearGeneration !== clearGenerationRef.current) {
        return;
      }

      return errorCallback(VideoUploadError.GET_PRE_SIGN_LINKS);
    }
  };

  const handleExpiredTask = (task: QueueVideoTask) => {
    removeTask(task.id);
    return handleMultiMultiPartUpload(task, task.callback, task.onError, task.onProgress);
  };

  /**
   * Updates an existing task status in the upload queue with new information.
   */
  const updateTaskStatus = (taskConnectionFileId: string, status: QueueVideoTask['status']) =>
    dispatch({type: ActionKind.UPDATE_TASK_STATUS, payload: {status, taskConnectionFileId}});

  /**
   * Updates a specific job within an upload task.
   */
  const updateTaskJob = (
    taskConnectionFileId: string,
    updatedJob: Partial<QueueVideoTaskJob> & {id: string}
  ) =>
    dispatch({type: ActionKind.UPDATE_TASK_JOB, payload: {job: updatedJob, taskConnectionFileId}});

  /**
   * Processes a single job within an upload task by uploading the corresponding chunk of the file.
   * It updates the job's status to 'ONGOING' at the start and to 'DONE' upon completion.
   */
  const processJob = (
    job: QueueVideoTaskJob,
    task: QueueVideoTask,
    getChunk: GetChunkFn,
    signal: AbortSignal
  ) => {
    updateTaskJob(task.connectionFileId, {id: job.id, status: 'ONGOING'});

    return handleUploadChunk(task, job, getChunk, signal)
      .then(({etag}) => {
        updateTaskJob(task.connectionFileId, {id: job.id, status: 'DONE', etag});

        // A chunk that lands after its task was removed or the queue cleared is not progress
        // anybody is waiting on — same reasoning as the cancelled-upload branch below.
        if (!signal.aborted) {
          task.onProgress?.(task);
        }
      })
      .catch((error: Error) => {
        // A cancelled upload is not a failure: the task was removed or the queue cleared on
        // purpose and its state is already gone. Reporting it would raise an error for work
        // nobody is waiting on any more.
        if (signal.aborted) {
          return;
        }

        // Reset to PENDING so the job is retried on the next processTask cycle.
        // Leaving it ONGOING would permanently block processTask (hasOngoingJobs guard).
        updateTaskJob(task.connectionFileId, {id: job.id, status: 'PENDING'});
        handleError(VideoUploadError.UNABLE_TO_UPLOAD_CHUNK, task.connectionFileId, error);
        task.onError?.(task);
      });
  };

  /**
   * Initiates the processing of an upload task, running up to PARALLEL_UPLOADS jobs concurrently.
   * The effect loop in VideoUploadQueueProvider drives repeated calls via the PENDING status toggle.
   * Once every chunk is DONE, `onCompleteCallback` receives the attempt's presign id
   * (`connectionFileId`) before the completion call is issued.
   */
  const processTask = (
    task: QueueVideoTask,
    getChunk: GetChunkFn,
    onCompleteCallback: (connectionFileId: string) => void
  ) => {
    // Every task gets its controller in `addTask` and loses it only on removal or a clear, so a
    // missing controller means the task is gone. The task passed in can still be one the queue
    // has already dropped: the provider's effect may flush with the task list of a render from
    // before the clear. Such a task must not start chunks, nor complete and run its callback — a
    // connect fired after a logout would go out under whichever session is current by then.
    const signal = abortControllersRef.current.get(task.id)?.signal;

    if (isNil(signal) || signal.aborted) {
      return;
    }

    if (task.status === 'PENDING') {
      updateTaskStatus(task.connectionFileId, 'ONGOING');
    }

    const pendingJobs = task.jobs.filter((job) => job.status === 'PENDING');
    const hasOngoingJobs = task.jobs.some((job) => job.status === 'ONGOING');

    if (pendingJobs.length > 0) {
      const batch = pendingJobs.slice(0, PARALLEL_UPLOADS);
      return Promise.all(batch.map((job) => processJob(job, task, getChunk, signal))).finally(() =>
        updateTaskStatus(task.connectionFileId, 'PENDING')
      );
    }

    if (hasOngoingJobs) {
      return;
    }

    updateTaskStatus(task.connectionFileId, 'DONE');
    // Reported per attempt (presign id), not per file: a retry of the same file is a new attempt
    // and must be processed again.
    onCompleteCallback(task.connectionFileId);

    // The completion call cannot be aborted, but the task's signal still tells a deliberate
    // removal or clear apart from a genuine failure — same reasoning as in `processJob`. It also
    // keeps a late success from running the callback of a task the queue has already dropped.
    return handleTaskCompleted(task.connectionFileId, task.jobs)
      .then(() => {
        if (signal.aborted) {
          return;
        }

        task.callback(task);
        removeTask(task.id);
      })
      .catch((error: Error) => {
        if (signal.aborted) {
          return;
        }

        handleError(VideoUploadError.UNABLE_TO_COMPLETE_TASK, task.connectionFileId, error);
        task.onError?.(task);
      });
  };

  return {
    tasks,
    removeTask,
    clearTasks,
    processTask,
    uploadProgress,
    handleExpiredTask,
    handleMultiMultiPartUpload,
  };
}
